import React, { useRef } from 'react';

/**
 * Logo upload with mini LCD screen preview (using app's bg-screen-* classes)
 * and upload-dropzone style button — both on the same row.
 *
 * Layout:
 *   [ LCD Screen (× overlay) ] [ upload dropzone (dots) ]
 *
 * Uses .bg-screen-bezel + .bg-screen-inner (same as custom backgrounds module)
 * and .upload-dropzone (same as background upload).
 * Delete button uses .bg-screen-delete (same as custom background thumbnails).
 *
 * @param {Object}    props
 * @param {Function}  props.onSelect       — (file: File) => void
 * @param {boolean}   [props.isLoading]    — Show uploading state
 * @param {string}    [props.accept]       — File filter (default: "image/*")
 * @param {string}    [props.label]        — Upload button label (default: "Load Logo Image")
 * @param {ReactNode} props.preview        — Preview content (e.g., <SlideLogo />)
 * @param {boolean}   [props.hasCustom]    — Whether a custom image is set (shows × delete)
 * @param {Function}  [props.onReset]      — Called when user clicks × to remove logo
 */
export function LogoUpload({
  onSelect,
  isLoading = false,
  accept = 'image/*',
  label = 'Load Logo Image',
  preview,
  hasCustom = false,
  onReset,
}) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onSelect(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex items-stretch gap-2.5">
      {/* Mini LCD screen — logo preview (bg-screen-* classes from backgrounds module) */}
      <div
        className="bg-screen-bezel shrink-0"
        style={{ width: '72px', aspectRatio: '1', cursor: 'default', position: 'relative' }}
      >
        <div
          className="bg-screen-inner"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {preview}
        </div>
        {/* × delete overlay — same pattern as custom background thumbnails */}
        {hasCustom && onReset && (
          <div
            className="bg-screen-delete"
            onClick={(e) => { e.stopPropagation(); onReset(); }}
            title="Remove custom logo"
          >
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}
      </div>

      {/* Upload dropzone — dot grid pattern (matches background upload) */}
      <div className="flex-1 flex flex-col gap-1.5">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="hidden"
        />
        <div
          onClick={() => !isLoading && inputRef.current?.click()}
          className={`upload-dropzone flex-1${isLoading ? ' upload-dropzone-loading' : ''}`}
          style={{ display: 'flex', alignItems: 'center', minHeight: 0, padding: '10px 16px' }}
        >
          <div className="upload-dropzone-content" style={{ width: '100%' }}>
            <svg className="upload-dropzone-icon" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <div className="upload-dropzone-text">
              {isLoading ? 'Uploading...' : label}
            </div>
            <div className="upload-dropzone-sub">PNG / SVG / JPG</div>
          </div>
        </div>
      </div>
    </div>
  );
}
